import React, {useCallback, useEffect, useRef, useState} from "react";

// Keep the source URL out of the media element until playback is requested.
const DeferredVideo = ({src, type, ...videoProps}) => {
    const videoRef = useRef(null);
    const sourceRef = useRef(null);
    const [playing, setPlaying] = useState(false);
    const [manualPlayback, setManualPlayback] = useState(false);

    const play = useCallback(() => {
        const video = videoRef.current;
        if (!sourceRef.current.hasAttribute("src")) {
            sourceRef.current.setAttribute("src", src);
            video.load();
        }
        const promise = video.play();
        if (promise) promise.catch(() => {}); // The play button remains available if autoplay is blocked.
    }, [src]);

    useEffect(() => {
        const video = videoRef.current;
        const desktop = window.matchMedia("(min-width: 768px)");
        let visible = false;
        const updatePlayback = () => {
            if (visible && desktop.matches) play();
            else video.pause();
        };
        const observer = typeof IntersectionObserver === "undefined" ? null : new IntersectionObserver(([entry]) => {
            visible = entry.isIntersecting;
            if (!visible) video.pause();
            else if (desktop.matches) play();
        });
        observer?.observe(video.parentElement);
        if (desktop.addEventListener) desktop.addEventListener("change", updatePlayback);
        else desktop.addListener(updatePlayback);

        return () => {
            observer?.disconnect();
            if (desktop.removeEventListener) desktop.removeEventListener("change", updatePlayback);
            else desktop.removeListener(updatePlayback);
            video.pause();
        };
    }, [play]);

    return (
        <>
            <video
                {...videoProps}
                ref={videoRef}
                preload="none"
                loop
                muted
                playsInline
                controls={manualPlayback}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
            >
                <source ref={sourceRef} type={type} />
            </video>
            {!playing && (
                <button
                    className="deferred-video-play"
                    type="button"
                    aria-label="Play video"
                    onClick={() => {
                        setManualPlayback(true);
                        play();
                    }}
                >
                    <span aria-hidden="true">▶</span> Play
                </button>
            )}
        </>
    );
};

export default DeferredVideo;
